import React, { useEffect, useState } from "react";
import { rolesApi, errorMessage } from "../adminApi";
import { useAuth, hasRole } from "../../context/AuthContext";
import { useAdminToast } from "../components/Toast";
import { Card, Table, Badge, Button, Modal, Loading, ErrorState, PageHeader, Input } from "../components/ui";

export default function RolesPage() {
  const { user } = useAuth();
  const toast = useAdminToast();
  const canMutate = hasRole(user, "SUPER_ADMIN"); // mutasi role hanya SUPER_ADMIN
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // {mode, data}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await rolesApi.list();
      setRows(res.rows || res || []);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (form) => {
    setBusy(true);
    try {
      if (editing.mode === "create") {
        await rolesApi.create({ code: form.code, name: form.name });
        toast.success("Role dibuat");
      } else {
        await rolesApi.update(editing.data.id, { name: form.name });
        toast.success("Role diperbarui");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Hapus role ${r.name}?`)) return;
    try {
      await rolesApi.remove(r.id);
      toast.success("Role dihapus");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const columns = [
    { key: "code", header: "Kode", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
    { key: "name", header: "Nama", render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
    { key: "users", header: "Jml User", render: (r) => <Badge color="gray">{r.user_count ?? 0}</Badge> },
    { key: "sys", header: "Tipe", render: (r) => (r.is_system ? <Badge color="purple">Sistem</Badge> : <Badge color="blue">Custom</Badge>) },
    {
      key: "act",
      header: "",
      render: (r) =>
        canMutate && (
          <div className="flex gap-1.5 justify-end">
            <Button size="sm" variant="secondary" onClick={() => setEditing({ mode: "edit", data: r })}>Edit</Button>
            {!r.is_system && (
              <Button size="sm" variant="danger" onClick={() => remove(r)} disabled={r.user_count > 0}>Hapus</Button>
            )}
          </div>
        ),
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Role"
        subtitle="Role menentukan hak akses. 5 role sistem tidak bisa dihapus."
        action={canMutate && <Button onClick={() => setEditing({ mode: "create", data: { code: "", name: "" } })}>+ Tambah Role</Button>}
      />
      <div className="flex-1 min-h-0 flex flex-col">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : (
          <Card className="flex-1 min-h-0 overflow-hidden">
            <Table columns={columns} rows={rows} empty="Belum ada role." />
          </Card>
        )}
      </div>

      <RoleFormModal editing={editing} busy={busy} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}

function RoleFormModal({ editing, busy, onClose, onSave }) {
  const [form, setForm] = useState({ code: "", name: "" });
  useEffect(() => {
    if (editing) setForm({ code: editing.data.code || "", name: editing.data.name || "" });
  }, [editing]);
  if (!editing) return null;
  const isCreate = editing.mode === "create";

  return (
    <Modal
      open={!!editing}
      onClose={onClose}
      title={isCreate ? "Tambah Role" : "Edit Role"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Batal</Button>
          <Button onClick={() => onSave(form)} disabled={busy || !form.name || (isCreate && !form.code)}>{busy ? "Menyimpan…" : "Simpan"}</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Kode (HURUF_BESAR)"
          value={form.code}
          disabled={!isCreate}
          onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
          placeholder="mis. SUPERVISOR"
        />
        <Input label="Nama" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </div>
    </Modal>
  );
}
